<template>
  <form
    ref="form"
    @keyup.enter="submit(step === 'request' ? 'request' : 'reset')"
    v-on:submit.prevent=""
  >
    <!-- Step 1 (input email) -->
    <div v-if="step === 'request'" key="FORM__REQUEST">
      <h1>Forgot password?</h1>

      <div class="row row--separator row--separatorTop">
        <div class="row__full">
          <p v-if="errorMessage" class="row__error" v-html="errorMessage" />
          <p v-else>No problem! Just write your email address below and we will send you a link to reset your password.</p>
        </div>
      </div>

      <div class="row row--separator">
        <div class="row__full">
          <FormInput
            :value="email"
            ref-name="email"
            placeholder="you@company.com"
            label="Email"
            alt-label="Valid email is required"
            type="email"
            class="form__input"
            autocomplete="email"
            iconSize="md"
            :icon="icon.email"
            :error="errors.email"
            :status="status.email"
            @inputChanged="setFieldValue($event, 'email')"
            @unfocused="validate('email')"
          />
        </div>
      </div>

      <div class="row row--separator">
        <div class="row__full">
          <FormSubmit
            :status="formStatus"
            icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13 18v-4h-7v-4h7v-4l6 6-6 6zm-1-16c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12z"/></svg>'
            @click="submit('request')"
          >
            {{ formStatus === 'success' ? 'Success' : 'Send email' }}
          </FormSubmit>
        </div>
      </div>
    </div>

    <!-- Step 2 (request thank you) -->
    <div v-if="step === 'requestOk'" key="FORM__REQUEST_OK">
      <h1>Thank you</h1>

      <div class="row row--separator row--separatorTop">
        <div class="row__full">
          <p>Link to reset your password has been sent to the email you provided! Please check your inbox and spam folders.</p>
        </div>
      </div>
    </div>

    <!-- Step 3 (change password) -->
    <div v-if="step === 'reset'" key="FORM__RESET">
      <h1>Reset your password</h1>

      <div class="row row--separator row--separatorTop">
        <div class="row__full">
          <p v-if="errorMessage" class="row__error" v-html="errorMessage" />
          <p v-else>Please change your password here. After change your previous authorization tokens are no longer valid and you have to relogin on all devices.</p>
        </div>
      </div>

      <div class="row row--separator">
        <div class="row__full">
          <FormInput
            :value="password"
            ref-name="password"
            placeholder="Enter your new password here"
            label="Password"
            alt-label="Minimum of 8 characters"
            class="form__input"
            type="password"
            autocomplete="new-password"
            iconSize="md"
            :icon="icon.password"
            :error="errors.password"
            :status="status.password"
            @inputChanged="setFieldValue($event, 'password')"
            @unfocused="validate('password')"
          />
        </div>
      </div>

      <div class="row">
        <div class="row__full">
          <FormInput
            :value="passwordConfirmation"
            ref-name="passwordConfirmation"
            placeholder="Enter your new password here"
            label="Password confirmation"
            alt-label="Minimum of 8 characters"
            class="form__input"
            type="password"
            autocomplete="new-password"
            :icon="icon.password"
            :error="errors.passwordConfirmation"
            :status="status.passwordConfirmation"
            @inputChanged="setFieldValue($event, 'passwordConfirmation')"
            @unfocused="validate('password')"
          />
        </div>
      </div>

      <div class="row row--separator">
        <div class="row__full">
          <FormSubmit
            :status="formStatus"
            icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13 18v-4h-7v-4h7v-4l6 6-6 6zm-1-16c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12z"/></svg>'
            @click="submit('reset')"
          >
            {{ formStatus === 'loading' ? 'Loading...' : 'Set new password' }}
          </FormSubmit>
        </div>
      </div>
    </div>

    <!-- Step 4 (request thank you) -->
    <div v-if="step === 'resetOk'" key="FORM__RESET_OK">
      <h1>Password changed!</h1>

      <div class="row row--separator row--separatorTop">
        <div class="row__full">
          <p>Your password has been succesfully changed. All previous logins in all devices have been provoked. You may now login to access your account.</p>
        </div>
      </div>

      <div class="row row--separator">
        <div class="row__full">
          <UtilityCtaButton
            icon='<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13 18v-4h-7v-4h7v-4l6 6-6 6zm-1-16c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12z"/></svg>'
            :round="true"
            color="blue"
            class="login__registerBtn"
            @click="goToLogin()"
          >
            Sign in
          </UtilityCtaButton>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import FormInput from '@/components/form/Input.vue'
import FormSubmit from '@/components/form/Submit.vue'
import UtilityCtaButton from '@/components/utility/CtaButton.vue'

export default {
  components: {
    FormInput,
    FormSubmit,
    UtilityCtaButton
  },
  data () {
    return {
      step: 'request',
      token: '',
      formLoading: false,
      formStatus: 'default',
      errorMessage: '',
      errors: {
        email: '',
        password: '',
        passwordConfirmation: ''
      },
      status: {
        email: false,
        password: false,
        passwordConfirmation: false
      },
      icon: {
        email: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 12.713l-11.985-9.713h23.971l-11.986 9.713zm-5.425-1.822l-6.575-5.329v12.501l6.575-7.172zm10.85 0l6.575 7.172v-12.501l-6.575 5.329zm-1.557 1.261l-3.868 3.135-3.868-3.135-8.11 8.848h23.956l-8.11-8.848z"/></svg>',
        password: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-5 7.723v2.277h-2v-2.277c-.595-.347-1-.984-1-1.723 0-1.104.896-2 2-2s2 .896 2 2c0 .738-.404 1.376-1 1.723zm-5-7.723v-4c0-2.206 1.794-4 4-4 2.205 0 4 1.794 4 4v4h-8z"/></svg>'
      },
      email: '',
      password: '',
      passwordConfirmation: ''
    }
  },
  mounted () {
    if (this.$route.params.token) {
      this.token = this.$route.params.token
      this.step = 'reset'
    }
  },
  methods: {
    async submit (type) {
      this.formLoading = true
      this.formStatus = 'loading'
      let formData = {}
      this.errorMessage = ''

      if (this.validate()) {
        if (type === 'request') {
          formData = {
            step: 'request',
            email: this.email
          }
        } else if (type === 'reset') {
          formData = {
            step: 'reset',
            token: this.token,
            password: this.password
          }
        }

        try {
          await this.$store.dispatch('user/forgotPassword', formData)
          this.formLoading = false
          this.formStatus = 'default'
          this.step = type === 'request' ? 'requestOk' : 'resetOk'
        } catch (error) {
          this.formLoading = false
          this.formStatus = 'default'
          this.step = type === 'request' ? 'request' : 'reset'
          if (error.data) {
            if (error.data.errors && error.data.errors.token) {
              this.errorMessage = 'Invalid token.'
            } else {
              this.errorMessage = error.data.message ? error.data.message : ''
              this.errors = error.data.errors ? error.data.errors : {
                email: '',
                password: '',
                passwordConfirmation: ''
              }
            }
          }
        }
      } else {
        this.formLoading = false
        this.formStatus = 'default'
      }
    },
    setFieldValue (value, field) {
      this[field] = value
    },
    validate () {
      let valid = true
      this.errors = {
        email: '',
        password: '',
        passwordConfirmation: ''
      }

      if (this.step === 'request') {
        if (this.email === '') {
          this.errors.email = 'Required field.'
          valid = false
        } else if (this.email.length > 255) {
          this.errors.email = 'Maximum of 255 characters.'
          valid = false
        } else if (this.email !== '') {
          let emailTest = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          if (!emailTest.test(this.email)) {
            this.errors.email = 'Email must be valid.'
            valid = false
          }
        }

        this.status.email = this.errors.email !== '' ? 'invalid' : 'valid'
      }

      if (this.step === 'reset') {
        if (this.password === '') {
          this.errors.password = 'Required field.'
          valid = false
        } else if (this.password.length < 8) {
          this.errors.password = 'Minimum of 8 characters.'
          valid = false
        }

        if (this.password !== this.passwordConfirmation) {
          this.errors.passwordConfirmation = 'Passwords must match.'
          valid = false
        }

        this.status.password = this.errors.password !== '' ? 'invalid' : 'valid'
        this.status.passwordConfirmation = this.errors.passwordConfirmation !== '' ? 'invalid' : 'valid'
      }

      return valid
    },
    goToLogin () {
      this.$router.push('/login')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

h1, h2 {
  @include font('header--xl', $white, 0.9);
  width: 90%;
  margin: 0 auto;
  text-align: center;

  @media screen and (min-width: $breakpointLg) {
    @include font('header--xxl', $white, 0.9);
    margin: 0 auto;
  }
}

p {
  @include font('content--md', $white);

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white);
  }
}

</style>
